import { createPinia } from 'pinia';
import { createApp, ref, type App as VueApp } from 'vue';

import {
  getActiveLocale,
  onLocaleChange,
  resolveInitialLocale,
  t as translate,
  type LocaleCode,
  type MessageKey,
  type MessageParams,
} from '@altkey/i18n';

import App from './App.vue';
import router from './router';

import './styles/main.css';

/**
 * ============================================================================
 * AltKey.app Mobile - Application Entry Point
 * ============================================================================
 * 1. Inizializza l'app Vue 3 tramite `createApp`, con Pinia (`createPinia`)
 *    come store globale.
 * 2. Monta il componente radice `App.vue` e il router mobile.
 * 3. Avvolge il modulo framework-agnostic `@altkey/i18n` in un plugin Vue,
 *    cosi' la lingua del telefono viene rilevata automaticamente e resa
 *    reattiva fin dal primo render.
 *
 * A differenza di `apps/desktop/src/main.ts`, questo entry point registra
 * anche il Service Worker della PWA (generato da `vite-plugin-pwa`, vedi
 * `vite.config.ts`), assente sul client Desktop.
 * ============================================================================
 */

const AltKeyI18nPlugin = {
  install(app: VueApp): void {
    resolveInitialLocale();

    const activeLocale = ref<LocaleCode>(getActiveLocale());

    onLocaleChange((locale) => {
      activeLocale.value = locale;
    });

    app.provide('altkeyLocale', activeLocale);

    app.config.globalProperties.$t = (key: MessageKey, params?: MessageParams): string =>
      translate(key, params);
    app.config.globalProperties.$locale = activeLocale;
  },
};

const app = createApp(App);
const pinia = createPinia();

app.use(pinia);
app.use(AltKeyI18nPlugin);
app.use(router);

app.mount('#app');

/**
 * ============================================================================
 * REGISTRAZIONE SERVICE WORKER (PWA)
 * ============================================================================
 * `virtual:pwa-register` e' il modulo virtuale esposto da `vite-plugin-pwa`.
 * Con `registerType: 'autoUpdate'` gia' configurato in `vite.config.ts`, il
 * Service Worker si aggiorna automaticamente in background; qui gestiamo
 * solo il logging degli eventi di ciclo di vita, senza bloccare l'avvio
 * dell'app se la registrazione fallisse.
 */
if (import.meta.env.PROD && 'serviceWorker' in navigator) {
  void import('virtual:pwa-register').then(({ registerSW }) => {
    registerSW({
      immediate: true,
      onRegisteredSW(swUrl) {
        console.info(`[AltKey] Service Worker registrato: ${swUrl}`);
      },
      onRegisterError(error) {
        console.error('[AltKey] Registrazione Service Worker fallita:', error);
      },
      onOfflineReady() {
        console.info("[AltKey] App pronta per l'uso offline");
      },
    });
  });
}

declare module 'vue' {
  interface ComponentCustomProperties {
    $t: (key: MessageKey, params?: MessageParams) => string;
    $locale: import('vue').Ref<LocaleCode>;
  }
}